import React, { useState } from "react";
import { Alert, Pressable, StyleSheet, Text, View } from "react-native";
import * as Sentry from "@sentry/react-native";
import { Ionicons, MaterialIcons } from "@expo/vector-icons";

import UserAvatar from "@/Components/Media/User/Images/UserAvatar";
import { formatDistanceStr } from "@/formatting/distance";
import { formatLocationAddress } from "@/formatting/location";
import { formatRelationshipString } from "@/formatting/relationship";
import {
  RelationshipType,
  type UserRelationship,
} from "@/schemas/user/relationship";
import RelationshipService from "@/services/user/relationship";
import type { AppUserSession } from "@/schemas/auth/session";
import { themes } from "@/Components/Styling/themeConfigs";

type Props = {
  session: AppUserSession;
  user: UserRelationship;
  onClose?: () => void;
};

function UserListItem({ session, user, onClose }: Props): React.JSX.Element {
  const currentTheme = themes.morning;
  const separation = formatDistanceStr(user.profile.separation);
  const viewerIsTheUser = session.identity.username === user.profile.username;
  const [followerCount, setFollowerCount] = useState(
    user.profile.followerCount,
  );
  const [isCurrentlyFollowingState, setIsCurrentlyFollowingState] = useState(
    user.profile.isFollowing,
  );

  // todo; remove this logic
  // const isWithinSeparationLimit = (user.profile.separation?.value ?? 100) < 60;
  const isWithinSeparationLimit = false;

  const handleFollowButtonPress = async (
    isCurrentlyFollowing: boolean,
  ): Promise<void> => {
    try {
      const connectionService = new RelationshipService(session);
      await connectionService.toFollowOrNot(
        isCurrentlyFollowing,
        user.profile.username,
      );

      if (isCurrentlyFollowing) {
        setFollowerCount(Math.max(0, followerCount - 1));
      } else {
        setFollowerCount(followerCount + 1);
      }
      setIsCurrentlyFollowingState(!isCurrentlyFollowingState);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    }
  };

  const styles = StyleSheet.create({
    container: {
      alignItems: "center",
      flexDirection: "row",
      padding: 8,
      borderColor: currentTheme.borderColor,
      borderStyle: "solid",
      borderBottomWidth: 0.5,
    },
    infoContainer: {
      flex: 1,
      marginLeft: 10,
      marginTop: 5,
    },
    detailItem: {
      alignItems: "center",
      flexDirection: "row",
      marginBottom: 3,
    },
    itemPrimaryText: {
      fontSize: currentTheme.typography.h2.size.mobile,
      fontWeight: 600, // TODO: change themeConfig to use number instead of string for weight?
      color: currentTheme.typography.h2.color,
    },
    itemSecondaryText: {
      color: "#222",
      marginLeft: 5,
    },
    baseButton: {
      borderRadius: 20,
      marginLeft: "auto",
      paddingVertical: 10,
      paddingHorizontal: 20,
    },
    baseButtonText: {
      fontWeight: 600,
    },
    followButtonText: {
      color: currentTheme.button.textColor.primary,
    },
    followingButtonText: {
      color: currentTheme.button.textColor.secondary,
    },
  });

  return (
    <View style={styles.container}>
      <UserAvatar
        profilePress={{
          username: user.profile.username,
          ...(onClose && { onBeforeProfilePress: onClose }),
        }}
        avatar={user.profile.avatar}
        defaultAvatarColor={"black"}
        size={60}
      />

      <View style={styles.infoContainer}>
        {/* username */}
        <View style={styles.detailItem}>
          <Text style={styles.itemPrimaryText}>{user.profile.username}</Text>
        </View>

        {/* distance */}
        {isWithinSeparationLimit && !viewerIsTheUser ? (
          <View style={styles.detailItem}>
            <Ionicons name="navigate-outline" size={18} />
            <Text style={styles.itemSecondaryText}>{separation}</Text>
          </View>
        ) : (
          <View style={styles.detailItem}>
            <Ionicons name="location-outline" size={18} />
            <Text style={styles.itemSecondaryText}>
              {formatLocationAddress(user.profile.location)}
            </Text>
          </View>
        )}

        {/* follower/following count */}
        {followerCount >= user.profile.followingCount ? (
          <View style={styles.detailItem}>
            <MaterialIcons name="people-outline" size={18} />
            <Text style={styles.itemSecondaryText}>
              {formatRelationshipString(
                RelationshipType.FOLLOWER,
                user.profile.followerCount,
              )}
            </Text>
          </View>
        ) : (
          <View style={styles.detailItem}>
            <MaterialIcons name="person-add-alt" size={18} />
            <Text style={styles.itemSecondaryText}>
              {formatRelationshipString(
                RelationshipType.FOLLOWING,
                user.profile.followingCount,
              )}
            </Text>
          </View>
        )}
      </View>

      {/* button */}
      {/* TODO: create a UserItemButton Component or refactor ActionButton component? */}
      {viewerIsTheUser ? (
        <></> // no follow button display if it'is the viewer themself
      ) : isCurrentlyFollowingState ? (
        // todo: improve button color pressed vs not pressed
        <Pressable
          onPress={() => handleFollowButtonPress(isCurrentlyFollowingState)}
          style={({ pressed }) => [
            {
              backgroundColor: pressed
                ? "#DDD"
                : currentTheme.button.color.secondary,
            },
            styles.baseButton,
          ]}
        >
          <Text style={[styles.baseButtonText, styles.followingButtonText]}>
            Following
          </Text>
        </Pressable>
      ) : (
        <Pressable
          onPress={() => handleFollowButtonPress(isCurrentlyFollowingState)}
          style={({ pressed }) => [
            {
              backgroundColor: pressed
                ? "#00377b"
                : currentTheme.button.color.primary,
            },
            styles.baseButton,
          ]}
        >
          <Text style={[styles.baseButtonText, styles.followButtonText]}>
            Follow
          </Text>
        </Pressable>
      )}
    </View>
  );
}

export default UserListItem;
